import React, { useState, useCallback } from 'react';
import { Box, Paper, Typography, Button, Grid, Avatar, Link, useTheme } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import DownloadIcon from '@mui/icons-material/Download';
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const faviconSizes = [16, 32, 48, 64, 192, 512];

function FaviconGenerator() {
  const theme = useTheme();
  const [sourceImage, setSourceImage] = useState(null);
  const [favicons, setFavicons] = useState([]);
  const [error, setError] = useState('');
  const [bgColor, setBgColor] = useState('#ffffff');

  const generateFavicons = useCallback((imageSrc, backgroundColor) => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      const generated = faviconSizes.map(size => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, size, size);

        // Draw image centered and scaled to fit
        const hRatio = canvas.width / img.width;
        const vRatio = canvas.height / img.height;
        const ratio = Math.min(hRatio, vRatio);
        const centerShift_x = (canvas.width - img.width * ratio) / 2;
        const centerShift_y = (canvas.height - img.height * ratio) / 2;
        ctx.drawImage(img, 0, 0, img.width, img.height,
          centerShift_x, centerShift_y, img.width * ratio, img.height * ratio);

        return {
          size,
          url: canvas.toDataURL('image/png'),
        };
      });
      setFavicons(generated);
    };
  }, []);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      setError('Invalid file type. Please upload an image.');
      return;
    }
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSourceImage(e.target.result);
        generateFavicons(e.target.result, bgColor);
        setError('');
      };
      reader.onerror = () => {
        setError('Failed to read the file.');
        setSourceImage(null);
        setFavicons([]);
      };
      reader.readAsDataURL(file);
    }
  }, [generateFavicons, bgColor]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
  });

  const handleColorChange = (color) => {
    setBgColor(color);
    if (sourceImage) {
      generateFavicons(sourceImage, color);
    }
  };

  return (
    <>
      <SEO
        title="Favicon Generator | Create Web Icons | UmbrellaCo"
        description="Generate multiple sizes of favicons from a single uploaded image. Customize background colors and download standard web icon sizes for free."
        keywords="favicon generator, create favicon online, icon resizer, web app icons, ico maker"
        url="/color-image-tools/favicon-generator"
        jsonLd={createSoftwareAppSchema(
          'Favicon Generator',
          'Create perfectly sized favicons for all platforms from a single image.',
          'https://umbrellaco.in/color-image-tools/favicon-generator',
          ['Icon Generation', 'Web Design', 'Image Resizing']
        )}
      />
      <ToolPageContainer
        title="Favicon Studio"
        description="Upload an image to automatically generate standards-compliant favicons for all browser sizes and platforms."
      >      {!sourceImage ? (
        <Box
          {...getRootProps()}
          className={`drop-zone ${isDragActive ? 'drop-zone-active' : ''}`}
          sx={{ mt: 2, p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <input {...getInputProps()} />
          <PhotoFilterIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography>Drag & drop your logo here, or click to select</Typography>
          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        </Box>
      ) : (
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6">Customize</Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <img src={sourceImage} alt="Source" style={{ width: '100%', height: 'auto', borderRadius: '4px' }} />
              <Box sx={{ mt: 2 }}>
                <Typography gutterBottom>Background Color</Typography>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid #ccc', cursor: 'pointer' }}
                />
              </Box>
              <Button onClick={() => setSourceImage(null)} sx={{ mt: 2 }}>
                Start Over
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h6">Live Previews</Typography>

            <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={favicons.find(f => f.size === 16)?.url} sx={{ width: 16, height: 16 }} variant="square" />
                <Typography variant="body2">My Awesome Site</Typography>
              </Box>
            </Paper>
            <Typography variant="caption" color="text.secondary">Browser Tab Preview</Typography>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              {favicons.map(favicon => (
                <Grid item key={favicon.size}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar src={favicon.url} sx={{ width: favicon.size, height: favicon.size }} variant="rounded" />
                    <Typography variant="caption" sx={{ mt: 1 }}>{favicon.size}x{favicon.size}</Typography>
                    <Link href={favicon.url} download={`favicon-${favicon.size}.png`} variant="body2">
                      <DownloadIcon fontSize="small" />
                    </Link>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
      </ToolPageContainer>
    </>
  );
}

export default FaviconGenerator;